import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  InputBase,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { GetAllOrganizations } from '../../Api/GetOrganization';
import {
  ConnectStripeCustomer,
  RepointStripeCustomer,
  GetBillingOverview,
  GetSubscriptionStatus,
} from '../../Api/Billing';
import OrgEntitlementsPanel from './OrgEntitlementsPanel';
import UnlockCodesTab from './UnlockCodesTab';
import SubscriptionOverviewPanel from './SubscriptionOverviewPanel';
import OrgSelector from './OrgSelector';
import { DIALOG_PAPER_SX, DIALOG_CONTENT_SX, DIALOG_ACTIONS_SX, FIELD_CONFIG } from './dialogStyles';
import pillTabBar_26 from '../StandardComponents/pillTabBar_26/pillTabBar_26';
import StandardDialogHeader from '../StandardComponents/StandardDialog/StandardDialogHeader';
import StandardInputField from '../StandardComponents/StandardInputField/StandardInputField';
import '../../Components/Common/GlobalStyles.css';

const PillTabBar_26 = pillTabBar_26;

const BILLING_TABS = [
  { value: 0, label: 'Customer Management' },
  { value: 1, label: 'Bulk Repoint' },
  { value: 2, label: 'Subscription Overview' },
  { value: 3, label: 'Unlock Codes' },
];

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const buildPartialOverview = (org, subscription) => ({
  subscription,
  zavior_org: {
    id: org.id,
    organization_name: org.organization_name,
    entity_name: org.entity_name,
    incorporation_number: org.incorporation_number,
    industry: org.industry,
    country: org.country,
    website: org.website,
    phone_number: org.phone_number,
    stripe_customer_id: org.stripe_customer_id,
    is_paid_client: org.is_paid_client,
    onboarding_completed: org.onboarding_completed,
    org_status: org.status,
  },
});

const filterOrgs = (orgs, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return orgs;
  return orgs.filter((org) => {
    const name = (org.organization_name || '').toLowerCase();
    const id = (org.id || '').toLowerCase();
    const stripe = (org.stripe_customer_id || '').toLowerCase();
    return name.includes(q) || id.includes(q) || stripe.includes(q);
  });
};

function renderSearchBox({ value, onChange, onClear, dense = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '0 8px',
        width: dense ? '152px' : '180px',
        height: dense ? '30px' : '36px',
        transition: 'width 0.3s ease',
        '& .MuiInputBase-root': { width: '100%' },
        '&:focus-within': {
          width: dense ? '254px' : '300px',
          borderColor: '#1D3DFC',
        },
      }}
    >
      <SearchIcon sx={{ color: '#6B7280', fontSize: dense ? 17 : 20 }} />
      <InputBase
        placeholder="Search..."
        inputProps={{ 'aria-label': 'search' }}
        sx={{
          fontSize: dense ? '0.74rem' : '0.875rem',
          padding: '2px 4px',
          flex: 1,
          '& input': {
            '&::placeholder': { color: '#9CA3AF' },
          },
        }}
        value={value}
        onChange={onChange}
      />
      {value && (
        <IconButton
          size="small"
          onClick={onClear}
          sx={{
            padding: '2px',
            color: '#6B7280',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#1D3DFC',
            },
          }}
        >
          <ClearIcon sx={{ fontSize: dense ? 15 : 18 }} />
        </IconButton>
      )}
    </Box>
  );
}

export default function BillingAdmin() {
  const [tab, setTab] = useState(0);

  // ── Tab 1: Customer Management ──────────────────────────────────────────────
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Connect modal
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectOrg, setConnectOrg] = useState(null);
  const [connectInput, setConnectInput] = useState('');
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState('');

  // ── Tab 2: Bulk Repoint ─────────────────────────────────────────────────────
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [repointConfirmOpen, setRepointConfirmOpen] = useState(false);
  const [repointLoading, setRepointLoading] = useState(false);
  const [repointResult, setRepointResult] = useState(null);
  const [repointError, setRepointError] = useState('');

  // ── Tab 3: Subscription Overview ────────────────────────────────────────────
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [billingOverview, setBillingOverview] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState('');
  const [subNotice, setSubNotice] = useState('');

  const filteredOrgs = useMemo(() => filterOrgs(orgs, searchQuery), [orgs, searchQuery]);

  // ── Load orgs ───────────────────────────────────────────────────────────────
  const loadOrgs = useCallback(() => {
    setOrgsLoading(true);
    setOrgsError('');
    GetAllOrganizations()
      .then((res) => {
        const data = res?.data || res || [];
        setOrgs(Array.isArray(data) ? data : []);
      })
      .catch(() => setOrgsError('Failed to load organizations.'))
      .finally(() => setOrgsLoading(false));
  }, []);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  // ── Connect handlers ─────────────────────────────────────────────────────────
  const openConnect = (org) => {
    setConnectOrg(org);
    setConnectInput('');
    setConnectError('');
    setConnectOpen(true);
  };

  const handleConnect = () => {
    if (!connectOrg) return;
    setConnectLoading(true);
    setConnectError('');
    ConnectStripeCustomer(connectOrg.id, connectInput.trim())
      .then((res) => {
        const customerId = res?.data?.stripe_customer_id;
        if (customerId && connectOrg?.id) {
          setOrgs((prev) =>
            prev.map((o) =>
              o.id === connectOrg.id ? { ...o, stripe_customer_id: customerId } : o
            )
          );
        }
        setConnectOpen(false);
        loadOrgs();
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || 'Failed to connect customer.';
        setConnectError(msg);
      })
      .finally(() => setConnectLoading(false));
  };

  // ── Repoint handlers ─────────────────────────────────────────────────────────
  const handleRepoint = () => {
    setRepointLoading(true);
    setRepointError('');
    setRepointResult(null);
    RepointStripeCustomer(fromId.trim(), toId.trim())
      .then((res) => {
        setRepointResult(res?.data || res);
        setRepointConfirmOpen(false);
        setFromId('');
        setToId('');
        loadOrgs();
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || 'Repoint failed.';
        setRepointError(msg);
        setRepointConfirmOpen(false);
      })
      .finally(() => setRepointLoading(false));
  };

  // ── Subscription overview ─────────────────────────────────────────────────────
  const loadSubStatus = (org) => {
    setSelectedOrg(org);
    setBillingOverview(null);
    setSubError('');
    setSubNotice('');
    setSubLoading(true);

    GetBillingOverview(org.id)
      .then((res) => setBillingOverview(res?.data?.data || res?.data || null))
      .catch(async (err) => {
        const status = err?.response?.status;
        const apiError = err?.response?.data?.error || err?.response?.data?.message;

        // Dev/staging may not have the new overview endpoint deployed yet.
        if (status === 404) {
          try {
            const subRes = await GetSubscriptionStatus(org.id);
            const subscription = subRes?.data?.data || subRes?.data || null;
            setBillingOverview(buildPartialOverview(org, subscription));
            setSubNotice(
              'Billing overview API is not available on this environment yet. Showing Zavior org data and subscription status only. Deploy iam_service with /billing/overview to see live Stripe customer and payload details.'
            );
            return;
          } catch (fallbackErr) {
            const fallbackMsg = fallbackErr?.response?.data?.error || fallbackErr?.response?.data?.message;
            setSubError(fallbackMsg || 'Failed to load subscription status.');
            return;
          }
        }

        setSubError(apiError || 'Failed to load billing overview.');
      })
      .finally(() => setSubLoading(false));
  };

  // OrgEntitlementsPanel calls this after an access edit / grant / revoke: patch the
  // selected org (so the access chip updates) and reload the overview (consistency flags).
  const handleOrgEntitlementsChanged = (patch) => {
    if (!selectedOrg) return;
    loadSubStatus(patch ? { ...selectedOrg, ...patch } : selectedOrg);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          Billing Admin
        </Typography>
        <Tooltip title="Manage Stripe customer links, bulk repoints, and subscription overviews.">
          <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Super Admin and Tech Admin access only. Manages Stripe customer lifecycle for all organizations.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <PillTabBar_26
          className="pillTabBar_26-compact"
          tabs={BILLING_TABS}
          value={tab}
          onChange={setTab}
        />
      </Box>

      {/* ── TAB 1: Customer Management ── */}
      <TabPanel value={tab} index={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          {renderSearchBox({
            value: searchQuery,
            onChange: (e) => setSearchQuery(e.target.value),
            onClear: () => setSearchQuery(''),
          })}
          <Button
            variant="outlined"
            className="global-grey-outlined-button"
            startIcon={<RefreshIcon />}
            onClick={loadOrgs}
            disabled={orgsLoading}
          >
            Refresh
          </Button>
        </Box>

        {orgsError && <Alert severity="error" sx={{ mb: 2 }}>{orgsError}</Alert>}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Organization</strong></TableCell>
                <TableCell><strong>Org ID</strong></TableCell>
                <TableCell><strong>Stripe Customer</strong></TableCell>
                <TableCell align="right"><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgsLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery ? 'No organizations match your search.' : 'No organizations found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredOrgs.map((org) => (
                <TableRow key={org.id} hover>
                  <TableCell>{org.organization_name}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {org.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {org.stripe_customer_id ? (
                      <Chip
                        label={org.stripe_customer_id}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                      />
                    ) : (
                      <Chip label="Not Linked" size="small" color="default" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant={org.stripe_customer_id ? 'outlined' : 'contained'}
                      className={org.stripe_customer_id ? 'global-outlined-button' : 'global-primary-button'}
                      startIcon={<LinkIcon />}
                      onClick={() => openConnect(org)}
                    >
                      {org.stripe_customer_id ? 'Re-link' : 'Connect'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* ── TAB 2: Bulk Repoint ── */}
      <TabPanel value={tab} index={1}>
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 540 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Bulk Repoint Stripe Customer
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use this after merging duplicate Stripe customers in the Stripe Dashboard.
            All organizations pointing to the "From" customer will be updated to point to the "To" customer.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <StandardInputField
              type="text"
              name="from_customer_id"
              label="From Customer ID (Old)"
              value={fromId}
              onChange={(e) => setFromId(e.target.value)}
              config={{ ...FIELD_CONFIG, placeholder: 'cus_OLD...' }}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <StandardInputField
              type="text"
              name="to_customer_id"
              label="To Customer ID (New)"
              value={toId}
              onChange={(e) => setToId(e.target.value)}
              config={{ ...FIELD_CONFIG, placeholder: 'cus_NEW...' }}
            />
          </Box>

          {repointError && <Alert severity="error" sx={{ mb: 2 }}>{repointError}</Alert>}
          {repointResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {repointResult.updated_orgs} organization(s) updated from{' '}
              <code>{repointResult.from}</code> → <code>{repointResult.to}</code>
            </Alert>
          )}

          <Button
            variant="contained"
            className="global-primary-button"
            startIcon={<SyncAltIcon />}
            disabled={!fromId.trim() || !toId.trim() || repointLoading}
            onClick={() => setRepointConfirmOpen(true)}
          >
            Repoint All
          </Button>
        </Paper>
      </TabPanel>

      {/* ── TAB 3: Subscription Overview ── */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Org selector list */}
          <Box sx={{ width: 320, flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Select an Organization</Typography>
            <OrgSelector
              orgs={orgs}
              loading={orgsLoading}
              selectedOrg={selectedOrg}
              onSelect={loadSubStatus}
              searchPlaceholder="Search organizations..."
              renderSubtitle={(org) => org.stripe_customer_id || 'No Stripe Customer'}
              maxHeight={500}
            />
          </Box>

          {/* Subscription details + per-org seats & entitlements */}
          <Box sx={{ flex: 1, minWidth: 320 }}>
            {selectedOrg && (
              <OrgEntitlementsPanel
                org={selectedOrg}
                onOrgChanged={handleOrgEntitlementsChanged}
              />
            )}
            <SubscriptionOverviewPanel
              loading={subLoading}
              error={subError}
              notice={subNotice}
              overview={selectedOrg ? billingOverview : null}
              orgName={selectedOrg?.organization_name}
              org={selectedOrg}
              onReload={() => selectedOrg && loadSubStatus(selectedOrg)}
            />
          </Box>
        </Box>
      </TabPanel>

      {/* ── TAB 4: Unlock Codes ── */}
      <TabPanel value={tab} index={3}>
        <UnlockCodesTab />
      </TabPanel>

      {/* ── Connect Modal ── */}
      <Dialog
        open={connectOpen}
        onClose={() => !connectLoading && setConnectOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <StandardDialogHeader
          title="Connect Stripe Customer"
          onClose={() => !connectLoading && setConnectOpen(false)}
        />
        <DialogContent sx={DIALOG_CONTENT_SX}>
          {connectOrg && (
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 1.5, fontWeight: 500 }}>
              {connectOrg.organization_name}
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: '#374151', mb: 2 }}>
            Enter a Stripe Customer ID to link this organization, or leave blank to auto-search
            Stripe by organization metadata and create one if not found.
          </Typography>
          <StandardInputField
            type="text"
            name="stripe_customer_id"
            label="Stripe Customer ID (Optional)"
            value={connectInput}
            onChange={(e) => setConnectInput(e.target.value)}
            config={{ ...FIELD_CONFIG, placeholder: 'cus_...' }}
          />
          {connectError && <Alert severity="error" sx={{ mt: 2 }}>{connectError}</Alert>}
        </DialogContent>
        <DialogActions sx={DIALOG_ACTIONS_SX}>
          <Button onClick={() => setConnectOpen(false)} disabled={connectLoading} className="global-text-button">
            Cancel
          </Button>
          <Button
            variant="contained"
            className="global-primary-button"
            onClick={handleConnect}
            disabled={connectLoading}
            startIcon={connectLoading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
          >
            {connectInput.trim() ? 'Link Customer' : 'Auto-Connect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Repoint Confirmation Dialog ── */}
      <Dialog
        open={repointConfirmOpen}
        onClose={() => !repointLoading && setRepointConfirmOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <StandardDialogHeader
          title="Confirm Bulk Repoint"
          onClose={() => !repointLoading && setRepointConfirmOpen(false)}
        />
        <DialogContent sx={DIALOG_CONTENT_SX}>
          <Typography variant="body2" sx={{ color: '#374151' }}>
            This will update <strong>all organizations</strong> currently linked to:
          </Typography>
          <Box sx={{
            my: 1.5,
            p: 1.5,
            bgcolor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
          }}
          >
            From: <strong>{fromId}</strong><br />
            To: <strong>{toId}</strong>
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            This action cannot be undone automatically. Verify the Stripe customer IDs before proceeding.
          </Typography>
        </DialogContent>
        <DialogActions sx={DIALOG_ACTIONS_SX}>
          <Button onClick={() => setRepointConfirmOpen(false)} disabled={repointLoading} className="global-text-button">
            Cancel
          </Button>
          <Button
            variant="contained"
            className="global-danger-button"
            onClick={handleRepoint}
            disabled={repointLoading}
            startIcon={repointLoading ? <CircularProgress size={16} color="inherit" /> : <SyncAltIcon />}
          >
            Confirm Repoint
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
